import React, { useEffect, useState } from 'react';

interface TipicoStats {
  competitors: { id: string; name: string; qualifier: string }[];
  seasonName: string;
  seasonStats: {
    competitor1: any;
    competitor2: any;
  };
  headToHead: { summary: { competitor1Wins: number; competitor2Wins: number; draws: number } };
  competitorSummaries: {
    competitor1: { lastEvents: any[]; tendencies: string[] };
    competitor2: { lastEvents: any[]; tendencies: string[] };
  };
  winProbability: { outcomes: { name: string; probability: number }[] };
  competitorPerformances: {
    competitor1: any;
    competitor2: any;
  };
}

interface MatchStatsProps {
  matchId: string | number;
}

const ProgressBar = ({ left, center, right }: { left: number; center: number; right: number }) => {
  return (
    <div className="flex w-full h-[6px] gap-1 mt-2">
      <div className="bg-[#118833] rounded-l-sm" style={{ width: `${left}%` }}></div>
      <div className="bg-[#6b7280]" style={{ width: `${center}%` }}></div>
      <div className="bg-[#118833] rounded-r-sm" style={{ width: `${right}%` }}></div>
    </div>
  );
};

const FormBadge = ({ type }: { type: string }) => {
  const isWin = type === 'W';
  const isDraw = type === 'D';
  return (
    <div className={`w-6 h-6 flex items-center justify-center rounded-full text-white text-xs font-bold ${isWin ? 'bg-[#118833]' : isDraw ? 'bg-[#6b7280]' : 'bg-[#dc2626]'}`}>
      {isWin ? 'S' : isDraw ? 'U' : 'N'}
    </div>
  );
};

const ComparisonRow = ({ label, val1, val2, suffix = '' }: { label: string; val1: number; val2: number; suffix?: string }) => {
  const total = Math.max(val1 + val2, 1);
  const p1 = (val1 / total) * 100;
  const p2 = (val2 / total) * 100;

  return (
    <div className="flex flex-col py-3 border-b border-gray-100 last:border-0">
      <div className="flex justify-between items-center text-[15px] font-bold">
        <span className="w-16 text-left">{val1}{suffix}</span>
        <span className="text-gray-500 font-normal text-sm">{label}</span>
        <span className="w-16 text-right">{val2}{suffix}</span>
      </div>
      <div className="flex justify-center w-full gap-1 mt-2">
        <div className="w-1/2 flex justify-end">
          <div className={`h-[3px] rounded-l-sm ${val1 >= val2 ? 'bg-[#dc2626]' : 'bg-gray-400'}`} style={{ width: `${p1}%` }}></div>
        </div>
        <div className="w-1/2 flex justify-start">
          <div className={`h-[3px] rounded-r-sm ${val2 >= val1 ? 'bg-[#6b7280]' : 'bg-gray-400'}`} style={{ width: `${p2}%` }}></div>
        </div>
      </div>
    </div>
  );
};

const MatchItem = ({ event, currentTeamId }: { event: any; currentTeamId: string }) => {
  const isHome = event.competitors[0].id === currentTeamId;
  const ourScore = isHome ? event.homeScore : event.awayScore;
  const theirScore = isHome ? event.awayScore : event.homeScore;

  let outcome = 'D';
  if (ourScore > theirScore) outcome = 'W';
  if (ourScore < theirScore) outcome = 'L';

  const date = new Date(event.date);
  const formattedDate = `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear().toString().slice(2)}`;

  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-100 text-[14px]">
      <div className="text-gray-400 w-16">{formattedDate}</div>
      <div className="flex-1 flex items-center justify-center gap-2">
        <span className={`text-right w-24 ${isHome ? 'font-bold' : ''} truncate`}>{event.competitors[0].name}</span>
        <span className="font-bold bg-gray-100 px-2 py-0.5 rounded text-gray-800">{event.homeScore}:{event.awayScore}</span>
        <span className={`text-left w-24 ${!isHome ? 'font-bold' : ''} truncate`}>{event.competitors[1].name}</span>
      </div>
      <div className="w-6 flex justify-end">
        <FormBadge type={outcome} />
      </div>
    </div>
  );
};

export const MatchStats: React.FC<MatchStatsProps> = ({ matchId }) => {
  const [data, setData] = useState<TipicoStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [teamStatTab, setTeamStatTab] = useState<'leistung' | 'season'>('leistung');
  const [lastMatchesTab1, setLastMatchesTab1] = useState<'gesamt' | 'heim'>('gesamt');
  const [lastMatchesTab2, setLastMatchesTab2] = useState<'gesamt' | 'auswärts'>('gesamt');

  useEffect(() => {
    fetch(`https://sports.tipico.de/v1/statistics-fes/soccer/quick/de/${matchId}`)
      .then(res => {
        if (!res.ok) throw new Error('Failed');
        return res.json();
      })
      .then(d => {
        setData(d);
        setLoading(false);
      })
      .catch(() => {
        console.log('API failed, using fallback data');
        setData({ "competitors": [{ "id": "sr:competitor:279991", "name": "KF Dukagjini", "qualifier": "home" }, { "id": "sr:competitor:2443", "name": "FC Lugano", "qualifier": "away" }], "seasonName": "UEFA Conference League 26/27", "seasonStats": { "competitor1": { "averageGoals": 0, "averageGoalsConceded": 0, "currentOverUnderPercent": 0, "currentOver": 0, "currentUnder": 1 }, "competitor2": { "averageGoals": 0, "averageGoalsConceded": 0, "currentOverUnderPercent": 0, "currentOver": 0, "currentUnder": 1 } }, "headToHead": { "summary": { "competitor1Wins": 0, "competitor2Wins": 1, "draws": 0 } }, "competitorSummaries": { "competitor1": { "lastEvents": [{ "id": null, "date": "2026-07-23T18:30:00Z", "competitors": [{ "id": "sr:competitor:2443", "name": "FC Lugano", "qualifier": "home" }, { "id": "sr:competitor:279991", "name": "KF Dukagjini", "qualifier": "away" }], "homeScore": 1, "awayScore": 0 }, { "id": null, "date": "2026-05-31T14:00:00Z", "competitors": [{ "id": "sr:competitor:279991", "name": "KF Dukagjini", "qualifier": "home" }, { "id": "sr:competitor:280003", "name": "FC Ballkani", "qualifier": "away" }], "homeScore": 1, "awayScore": 0 }, { "id": null, "date": "2026-05-24T14:00:00Z", "competitors": [{ "id": "sr:competitor:1039703", "name": "KF Prishtina E Re", "qualifier": "home" }, { "id": "sr:competitor:279991", "name": "KF Dukagjini", "qualifier": "away" }], "homeScore": 2, "awayScore": 3 }, { "id": null, "date": "2026-05-21T18:00:00Z", "competitors": [{ "id": "sr:competitor:277837", "name": "KF Ferizaj", "qualifier": "home" }, { "id": "sr:competitor:279991", "name": "KF Dukagjini", "qualifier": "away" }], "homeScore": 1, "awayScore": 2 }, { "id": null, "date": "2026-05-17T14:00:00Z", "competitors": [{ "id": "sr:competitor:279991", "name": "KF Dukagjini", "qualifier": "home" }, { "id": "sr:competitor:277835", "name": "KF Drita", "qualifier": "away" }], "homeScore": 2, "awayScore": 0 }], "tendencies": ["L", "W", "W", "W", "W"] }, "competitor2": { "lastEvents": [{ "id": null, "date": "2026-07-26T14:30:00Z", "competitors": [{ "id": "sr:competitor:2443", "name": "FC Lugano", "qualifier": "home" }, { "id": "sr:competitor:2463", "name": "FC Vaduz", "qualifier": "away" }], "homeScore": 2, "awayScore": 1 }, { "id": null, "date": "2026-07-23T18:30:00Z", "competitors": [{ "id": "sr:competitor:2443", "name": "FC Lugano", "qualifier": "home" }, { "id": "sr:competitor:279991", "name": "KF Dukagjini", "qualifier": "away" }], "homeScore": 1, "awayScore": 0 }, { "id": null, "date": "2026-07-17T18:15:00Z", "competitors": [{ "id": "sr:competitor:2443", "name": "FC Lugano", "qualifier": "home" }, { "id": "sr:competitor:2463", "name": "FC Vaduz", "qualifier": "away" }], "homeScore": 1, "awayScore": 2 }, { "id": null, "date": "2026-07-10T16:00:00Z", "competitors": [{ "id": "sr:competitor:2443", "name": "FC Lugano", "qualifier": "home" }, { "id": "sr:competitor:5885", "name": "1. FC Heidenheim 1846", "qualifier": "away" }], "homeScore": 1, "awayScore": 0 }, { "id": null, "date": "2026-07-07T16:00:00Z", "competitors": [{ "id": "sr:competitor:5871", "name": "FC Memmingen", "qualifier": "home" }, { "id": "sr:competitor:2443", "name": "FC Lugano", "qualifier": "away" }], "homeScore": 0, "awayScore": 4 }], "tendencies": ["W", "W", "L", "W", "W"] } }, "seasonStandings": { "competitor1": null, "competitor2": null, "competitor1Home": null, "competitor2Away": null }, "winProbability": { "outcomes": [{ "name": "home_team_winner", "probability": 18 }, { "name": "away_team_winner", "probability": 58 }, { "name": "draw", "probability": 24 }] }, "competitorPerformances": { "competitor1": { "matchesAnalyzed": 10, "avgGoals": 1.3, "overTwoFivePercent": 40, "bttsPercent": 40, "firstHalfWinnerPercent": 0, "firstHalfOverZeroFive": 33, "secondHalfOverZeroFive": 78, "underFourFiveCardsPercent": null, "underTenFiveCornersPercent": null }, "competitor2": { "matchesAnalyzed": 10, "avgGoals": 1.9, "overTwoFivePercent": 70, "bttsPercent": 60, "firstHalfWinnerPercent": 50, "firstHalfOverZeroFive": 60, "secondHalfOverZeroFive": 90, "underFourFiveCardsPercent": 50, "underTenFiveCornersPercent": 75 } } } as any);
        setLoading(false);
      });
  }, [matchId]);

  if (loading) return <div className="p-8 text-center text-gray-500">Statistiken werden geladen...</div>;
  if (error || !data) return <div className="p-8 text-center text-red-500">Statistiken konnten nicht geladen werden.</div>;

  const home = data.competitors.find(c => c.qualifier === 'home');
  const away = data.competitors.find(c => c.qualifier === 'away');

  if (!home || !away) return null;

  const homeProb = data.winProbability.outcomes.find(o => o.name === 'home_team_winner')?.probability || 0;
  const awayProb = data.winProbability.outcomes.find(o => o.name === 'away_team_winner')?.probability || 0;
  const drawProb = data.winProbability.outcomes.find(o => o.name === 'draw')?.probability || 0;

  const h2h = data.headToHead?.summary || { competitor1Wins: 0, draws: 0, competitor2Wins: 0 };
  const totalH2h = h2h.competitor1Wins + h2h.draws + h2h.competitor2Wins;
  const h2hP1 = totalH2h ? (h2h.competitor1Wins / totalH2h) * 100 : 0;
  const h2hP2 = totalH2h ? (h2h.competitor2Wins / totalH2h) * 100 : 0;
  const h2hP3 = totalH2h ? (h2h.draws / totalH2h) * 100 : 0;

  const renderSlide1 = () => (
    <div className="flex-none w-full px-4 pt-4 pb-2">
      <h3 className="font-bold text-[16px] mb-4">Siegwahrscheinlichkeit</h3>
      <div className="flex justify-between text-[14px]">
        <div className="flex flex-col">
          <span className="text-gray-500">{home.name}</span>
          <span className="font-bold text-[18px]">{homeProb}%</span>
        </div>
        <div className="flex flex-col items-center">
          <span className="text-gray-500">Remis</span>
          <span className="font-bold text-[18px]">{drawProb}%</span>
        </div>
        <div className="flex flex-col items-end">
          <span className="text-gray-500">{away.name}</span>
          <span className="font-bold text-[18px]">{awayProb}%</span>
        </div>
      </div>
      <ProgressBar left={homeProb} center={drawProb} right={awayProb} />

      <h3 className="font-bold text-[16px] mt-6 mb-3">Form</h3>
      <div className="flex justify-between">
        <div className="flex gap-1 relative">
          {data.competitorSummaries.competitor1.tendencies.map((t, i, arr) => (
            <div key={i} className="relative">
              <FormBadge type={t} />
              {i === arr.length - 1 && <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-0.5 bg-red-500"></div>}
            </div>
          ))}
        </div>
        <div className="flex gap-1 relative">
          {data.competitorSummaries.competitor2.tendencies.map((t, i, arr) => (
            <div key={i} className="relative">
              <FormBadge type={t} />
              {i === arr.length - 1 && <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-0.5 bg-[#118833]"></div>}
            </div>
          ))}
        </div>
      </div>

      <h3 className="font-bold text-[16px] mt-6 mb-3">{data.seasonName}</h3>
      <div className="flex justify-between items-center text-[15px] font-bold mb-2">
        <span>#0</span>
        <span className="text-gray-500 font-normal">Tabelle</span>
        <span>#0</span>
      </div>
      <div className="flex justify-between items-center text-[15px] font-bold mb-2">
        <span>{data.seasonStats.competitor1.currentOverUnderPercent}% <span className="text-gray-400 font-normal text-sm">({data.seasonStats.competitor1.currentOver}/{data.seasonStats.competitor1.currentUnder})</span></span>
        <span className="text-gray-500 font-normal">Über 2,5 Tore</span>
        <span>{data.seasonStats.competitor2.currentOverUnderPercent}% <span className="text-gray-400 font-normal text-sm">({data.seasonStats.competitor2.currentOver}/{data.seasonStats.competitor2.currentUnder})</span></span>
      </div>
      <div className="flex justify-between items-center text-[15px] font-bold">
        <span>{data.seasonStats.competitor1.averageGoals}</span>
        <span className="text-gray-500 font-normal">Tore pro Spiel</span>
        <span>{data.seasonStats.competitor2.averageGoals}</span>
      </div>
    </div>
  );

  const renderSlide2 = () => {
    const p1 = data.competitorPerformances.competitor1;
    const p2 = data.competitorPerformances.competitor2;
    return (
      <div className="flex-none w-full px-4 pt-4 pb-2">
        <h3 className="font-bold text-[16px] mb-1">Teamleistung <span className="font-normal text-gray-500">Letzte {p1.matchesAnalyzed} Spiele</span></h3>
        <div className="flex justify-between text-[14px] text-gray-500 mb-2 mt-4">
          <span>{home.name}</span>
          <span>{away.name}</span>
        </div>
        <ComparisonRow label="Durchschn. erzielte Tore" val1={p1.avgGoals} val2={p2.avgGoals} />
        <ComparisonRow label="Über 2,5 Tore" val1={p1.overTwoFivePercent} val2={p2.overTwoFivePercent} suffix="%" />
        <ComparisonRow label="Beide Teams trafen" val1={p1.bttsPercent} val2={p2.bttsPercent} suffix="%" />
        <ComparisonRow label="Halbzeitsieger" val1={p1.firstHalfWinnerPercent} val2={p2.firstHalfWinnerPercent} suffix="%" />
        <ComparisonRow label="1. HZ über 0,5 Tore" val1={p1.firstHalfOverZeroFive} val2={p2.firstHalfOverZeroFive} suffix="%" />
        <ComparisonRow label="2. HZ über 0,5 Tore" val1={p1.secondHalfOverZeroFive} val2={p2.secondHalfOverZeroFive} suffix="%" />
      </div>
    );
  };

  const renderSlide3 = () => {
    return (
      <div className="flex-none w-full px-4 pt-4 pb-2">
        <h3 className="font-bold text-[16px] mb-4">Letzte Spiele</h3>
        {data.competitorSummaries.competitor1.lastEvents.slice(0, 3).map((ev, i) => (
          <MatchItem key={i} event={ev} currentTeamId={home.id} />
        ))}
        {data.competitorSummaries.competitor2.lastEvents.slice(0, 3).map((ev, i) => (
          <MatchItem key={i} event={ev} currentTeamId={away.id} />
        ))}
      </div>
    );
  };

  return (
    <>
      <div className="w-full bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden relative" style={{ display: modalOpen ? 'none' : 'block' }}>
        <div className="overflow-hidden">
          <div className="flex transition-transform duration-300 ease-in-out" style={{ transform: `translateX(-${activeSlide * 100}%)` }}>
            {renderSlide1()}
            {renderSlide2()}
            {renderSlide3()}
          </div>
        </div>

        <div className="flex justify-between items-center p-4">
          <div className="flex items-center gap-2">
            <button onClick={() => setActiveSlide(Math.max(0, activeSlide - 1))} className="text-gray-400 p-1">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            </button>
            <div className="flex gap-1.5">
              {[0, 1, 2].map(i => (
                <div key={i} className={`w-1.5 h-1.5 rounded-full ${activeSlide === i ? 'bg-gray-800' : 'bg-gray-300 border border-gray-400'}`} />
              ))}
            </div>
            <button onClick={() => setActiveSlide(Math.min(2, activeSlide + 1))} className="text-gray-400 p-1">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </button>
          </div>
          <button onClick={() => setModalOpen(true)} className="text-[#005a9e] font-semibold text-sm hover:underline">
            Alle Statistiken
          </button>
        </div>
      </div>

      {modalOpen && (
        <div className="absolute inset-0 z-[120] flex flex-col bg-[#f0f2f5] overflow-y-auto">
          <div className="bg-white px-4 py-4 sticky top-0 z-10 flex items-center justify-center border-b border-gray-200">
            <h2 className="font-bold text-[18px]">Statistiken</h2>
            <button onClick={() => setModalOpen(false)} className="absolute right-4 p-2 bg-gray-100 rounded-full hover:bg-gray-200">
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>

          <div className="p-4 space-y-4 max-w-2xl mx-auto w-full">

            {/* H2H */}
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <h3 className="font-bold text-[18px] mb-4">H2H</h3>
              <div className="flex justify-between text-[14px]">
                <div className="flex flex-col">
                  <span className="text-gray-500">{home.name}</span>
                  <span className="font-bold text-[18px]">{h2h.competitor1Wins}</span>
                </div>
                <div className="flex flex-col items-center">
                  <span className="text-gray-500">Remis</span>
                  <span className="font-bold text-[18px]">{h2h.draws}</span>
                </div>
                <div className="flex flex-col items-end">
                  <span className="text-gray-500">{away.name}</span>
                  <span className="font-bold text-[18px]">{h2h.competitor2Wins}</span>
                </div>
              </div>
              <ProgressBar left={h2hP1} center={h2hP3} right={h2hP2} />

              <h4 className="font-bold text-[14px] mt-6 mb-2">Vorherige Begegnungen</h4>
              {data.competitorSummaries.competitor2.lastEvents.filter(e => e.competitors.some((c: any) => c.id === home.id)).map((ev, i) => (
                <MatchItem key={i} event={ev} currentTeamId={home.id} />
              ))}
            </div>

            {/* Win Prob */}
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <h3 className="font-bold text-[18px] mb-4">Siegwahrscheinlichkeit</h3>
              <div className="flex justify-between text-[14px]">
                <div className="flex flex-col">
                  <span className="text-gray-500">{home.name}</span>
                  <span className="font-bold text-[18px]">{homeProb}%</span>
                </div>
                <div className="flex flex-col items-center">
                  <span className="text-gray-500">Remis</span>
                  <span className="font-bold text-[18px]">{drawProb}%</span>
                </div>
                <div className="flex flex-col items-end">
                  <span className="text-gray-500">{away.name}</span>
                  <span className="font-bold text-[18px]">{awayProb}%</span>
                </div>
              </div>
              <ProgressBar left={homeProb} center={drawProb} right={awayProb} />
            </div>

            {/* Teamstatistik */}
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <h3 className="font-bold text-[18px] mb-4">Teamstatistik</h3>
              <div className="flex gap-4 border-b border-gray-200 overflow-x-auto no-scrollbar">
                <button onClick={() => setTeamStatTab('leistung')} className={`pb-2 font-bold whitespace-nowrap ${teamStatTab === 'leistung' ? 'border-b-2 border-red-600' : 'text-gray-500'}`}>Teamleistung</button>
                <button onClick={() => setTeamStatTab('season')} className={`pb-2 font-bold whitespace-nowrap ${teamStatTab === 'season' ? 'border-b-2 border-red-600' : 'text-gray-500'}`}>{data.seasonName}</button>
              </div>

              {teamStatTab === 'leistung' && (
                <div className="mt-4">
                  <div className="flex justify-between text-[14px] text-gray-500 mb-2 mt-4">
                    <span>{home.name}</span>
                    <span>{away.name}</span>
                  </div>
                  <ComparisonRow label="Durchschn. erzielte Tore" val1={data.competitorPerformances.competitor1.avgGoals} val2={data.competitorPerformances.competitor2.avgGoals} />
                  <ComparisonRow label="Über 2,5 Tore" val1={data.competitorPerformances.competitor1.overTwoFivePercent} val2={data.competitorPerformances.competitor2.overTwoFivePercent} suffix="%" />
                  <ComparisonRow label="Beide Teams trafen" val1={data.competitorPerformances.competitor1.bttsPercent} val2={data.competitorPerformances.competitor2.bttsPercent} suffix="%" />
                  <ComparisonRow label="Halbzeitsieger" val1={data.competitorPerformances.competitor1.firstHalfWinnerPercent} val2={data.competitorPerformances.competitor2.firstHalfWinnerPercent} suffix="%" />
                  <ComparisonRow label="1. HZ über 0,5 Tore" val1={data.competitorPerformances.competitor1.firstHalfOverZeroFive} val2={data.competitorPerformances.competitor2.firstHalfOverZeroFive} suffix="%" />
                  <ComparisonRow label="2. HZ über 0,5 Tore" val1={data.competitorPerformances.competitor1.secondHalfOverZeroFive} val2={data.competitorPerformances.competitor2.secondHalfOverZeroFive} suffix="%" />
                </div>
              )}
              {teamStatTab === 'season' && (
                <div className="mt-4">
                  <div className="flex justify-between items-center text-[15px] font-bold mb-4 pt-2">
                    <span>#0</span>
                    <span className="text-gray-500 font-normal">Tabelle</span>
                    <span>#0</span>
                  </div>
                  <div className="flex justify-between items-center text-[15px] font-bold mb-4">
                    <span>{data.seasonStats.competitor1.currentOverUnderPercent}% <span className="text-gray-400 font-normal text-sm">({data.seasonStats.competitor1.currentOver}/{data.seasonStats.competitor1.currentUnder})</span></span>
                    <span className="text-gray-500 font-normal">Über 2,5 Tore</span>
                    <span>{data.seasonStats.competitor2.currentOverUnderPercent}% <span className="text-gray-400 font-normal text-sm">({data.seasonStats.competitor2.currentOver}/{data.seasonStats.competitor2.currentUnder})</span></span>
                  </div>
                  <div className="flex justify-between items-center text-[15px] font-bold pb-2">
                    <span>{data.seasonStats.competitor1.averageGoals}</span>
                    <span className="text-gray-500 font-normal">Tore pro Spiel</span>
                    <span>{data.seasonStats.competitor2.averageGoals}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Letzte Spiele Home */}
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <h3 className="font-bold text-[18px] mb-4">Letzte Spiele: {home.name}</h3>
              <div className="flex gap-4 border-b border-gray-200 mb-4">
                <button onClick={() => setLastMatchesTab1('gesamt')} className={`pb-2 font-bold ${lastMatchesTab1 === 'gesamt' ? 'border-b-2 border-red-600' : 'text-gray-500'}`}>Gesamt</button>
                <button onClick={() => setLastMatchesTab1('heim')} className={`pb-2 font-bold ${lastMatchesTab1 === 'heim' ? 'border-b-2 border-red-600' : 'text-gray-500'}`}>Heim</button>
              </div>
              {data.competitorSummaries.competitor1.lastEvents.map((ev, i) => (
                <MatchItem key={i} event={ev} currentTeamId={home.id} />
              ))}
            </div>

            {/* Letzte Spiele Away */}
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <h3 className="font-bold text-[18px] mb-4">Letzte Spiele: {away.name}</h3>
              <div className="flex gap-4 border-b border-gray-200 mb-4">
                <button onClick={() => setLastMatchesTab2('gesamt')} className={`pb-2 font-bold ${lastMatchesTab2 === 'gesamt' ? 'border-b-2 border-red-600' : 'text-gray-500'}`}>Gesamt</button>
                <button onClick={() => setLastMatchesTab2('auswärts')} className={`pb-2 font-bold ${lastMatchesTab2 === 'auswärts' ? 'border-b-2 border-red-600' : 'text-gray-500'}`}>Auswärts</button>
              </div>
              {data.competitorSummaries.competitor2.lastEvents.map((ev, i) => (
                <MatchItem key={i} event={ev} currentTeamId={away.id} />
              ))}
            </div>

            <div className="h-20" />
          </div>
        </div>
      )}
    </>
  );
};

export default MatchStats;
